import { getLandingPagePath } from '~/routes'

export function getLocaleFromUrl({ fallback = 'en', search } = {}) {
  try {
    const searchString = search ?? window?.location?.search ?? '';
    const params = new URLSearchParams(searchString);
    return params.get('locale') || fallback;
  } catch {
    return fallback;
  }
}

export const getDateRangeLabel = (type) => {
  const today = new Date();
  const endDate = new Date(today);

  const labelMap = {
    next_week: 'Next 7 days',
    next_month: 'Next 30 days',
    next_two_months: 'Next Two Months',
  };

  switch (type) {
    case 'next_week':
      endDate.setDate(today.getDate() + 7);
      break;
    case 'next_month':
      endDate.setMonth(today.getMonth() + 1);
      break;
    case 'next_two_months':
      endDate.setMonth(today.getMonth() + 2);
      break;
    default:
      return type
  }

  const format = (date) => {
    const options = {
      day: 'numeric',
      month: 'short',
      year: (date.getFullYear() !== today.getFullYear()) ? 'numeric' : undefined
    };
    return date.toLocaleDateString('en-US', options);
  };

  return `${labelMap[type]} (${format(today)} – ${format(endDate)})`;
}

// Get display value for dropdown
export const getDisplayValue = (key, value) => {
  const mappings = {
    send_on: {
      'Any Day': 'any_day',
      'Weekdays Only': 'weekdays_only',
      'any_day': 'Any Day',
      'weekdays_only': 'Weekdays Only',
    },
    track_activity: {
      'One Week': 'one_week',
      'Two Weeks': 'two_weeks',
      'Four Weeks': 'four_weeks',
      'one_week': 'One Week',
      'two_weeks': 'Two Weeks',
      'four_weeks': 'Four Weeks',
    },
    date_range: {
      'Next Week': 'next_week',
      'Next Month': 'next_month',
      'Next Two Months': 'next_two_months',
    }
  };

  return mappings[key]?.[value] || value;
}

export const parseDate = (date) => {
  if (!date) return null;
  
  if (date instanceof Date) {
    return date;
  } 
  
  if (typeof date === 'string') {
    // For date strings in 'YYYY-MM-DD' format, parse them as local dates to avoid timezone offset
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    } else {
      return new Date(date);
    }
  }
  
  return new Date(date);
}

export const formatDateToLongMonthAndDay = (date) => {
  if (!date) return ''

  const dateObj = parseDate(date);
  if (!dateObj) return '';
  
  const formatOptions = { day: 'numeric', month: 'long' }
  const formattedFrom = dateObj.toLocaleDateString('en-GB', formatOptions)
  return formattedFrom
}

export const formatDateToDDMMYYYY = (date) => {
  if (!date) return '';

  const dateObj = new Date(date);
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}-${month}-${year}`;
}

export const parseDifficultyLevels = (level) => {
  const mappings = {
    'basic': 'Basic',
    'intermediate': 'Intermediate',
    'expert': 'Expert',
  }

  return mappings[level] || level
}

// Config types for filters
export const CONFIG_TYPES = {
  DIFFICULTY_LEVELS: 'difficultyLevels',
  INDUSTRIES: 'industries',
  THREAT_TYPES: 'threatTypes'
}

export const parseFrequency = (frequency) => {
  const mappings = {
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'bi_annually': 'Bi-Annually',
  }

  return mappings[frequency] || frequency
}

export const getLandingPageUrl = (companyId, campaignId, emailTemplateId, locale = 'en') => {
  if (!companyId || !campaignId || !emailTemplateId) return null

  const baseUrl = getLandingPagePath(companyId, campaignId, emailTemplateId)
  const normalizedLocale = locale || 'en'

  return normalizedLocale !== 'en'
    ? `${baseUrl}?locale=${encodeURIComponent(normalizedLocale)}`
    : baseUrl
}
